import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as A from './api/planning.ts';
import { UnauthorizedError } from './api/client.ts';
import { keys } from './lib/mutate.ts';
import { Login } from './screens/Login.tsx';
import { Calendar } from './screens/Calendar.tsx';
import { Projects, ProjectDetail } from './screens/Projects.tsx';
import { MySchedule } from './screens/MySchedule.tsx';
import { HistoryScreen } from './screens/HistoryScreen.tsx';
import { Banner, Spinner } from './ui/bits.tsx';

type Tab = 'calendar' | 'projects' | 'mine' | 'history';

export function App() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('calendar');
  const [project, setProject] = useState<A.PlannerProject | null>(null);

  // WHOAMI IS THE SESSION CHECK. A 401 here is the app's only definition of "not signed in"; no other
  // screen decides that, and nothing reads a token to guess.
  const me = useQuery({ queryKey: keys.me, queryFn: A.whoami, retry: false });

  if (me.isPending) return <div className="login"><Spinner /></div>;
  if (me.error instanceof UnauthorizedError || (me.isError && !me.data)) {
    return <Login onSignedIn={() => qc.invalidateQueries()} />;
  }
  const who = me.data!;
  const planner = A.can.plan(who);

  return (
    <div className="app">
      <header className="topbar">
        <h1>Bruce Planning</h1>
        <nav className="tabs" role="tablist">
          {planner && <button role="tab" aria-selected={tab === 'calendar'} onClick={() => { setTab('calendar'); setProject(null); }}>Kalender</button>}
          {planner && <button role="tab" aria-selected={tab === 'projects'} onClick={() => setTab('projects')}>Werven</button>}
          <button role="tab" aria-selected={tab === 'mine'} onClick={() => setTab('mine')}>Mijn planning</button>
          {/* THE DECISION LOG, BY PERMISSION. `planner_events.read` is held by `planning_owner` —
              Alex and Matti — and not by the `planner` role the operational lead also carries. The
              tab is absent rather than disabled for everybody else: a control you may never use is
              furniture, and this one would invite the question "why not me". */}
          {who.permissions.includes('planner_events.read') && (
            <button role="tab" aria-selected={tab === 'history'} onClick={() => setTab('history')}>Historie</button>
          )}
        </nav>
        <span className="spacer" />
        <span className="faint hide-sm">{who.displayName}</span>
        <button className="btn small" onClick={async () => { await A.logout(); qc.clear(); location.reload(); }}>
          Afmelden
        </button>
      </header>
      <main className="main">
        {!planner && tab !== 'mine' && (
          <Banner kind="info">Je hebt geen planningsrechten. Je ziet je eigen planning.</Banner>
        )}
        {tab === 'calendar' && planner && <Calendar me={who} />}
        {tab === 'projects' && planner && (project
          ? <ProjectDetail project={project} onBack={() => setProject(null)} />
          : <Projects onOpen={setProject} />)}
        {tab === 'mine' && <MySchedule />}
        {tab === 'history' && who.permissions.includes('planner_events.read') && <HistoryScreen />}
      </main>
    </div>
  );
}
